<!-- src/components/rota/ShiftEditorModal.vue -->
<template>
  <div ref="modalEl" class="modal core-materialize-modal core-materialize-modal--medium">
    <div class="modal-content">
      <h5 style="margin-top: 0">Shift</h5>

      <div
        v-if="localError"
        class="card-panel red lighten-5 red-text text-darken-2"
        style="padding: 10px"
      >
        {{ localError }}
      </div>

      <div class="row" style="margin-bottom: 0">
        <div class="input-field col s12 m6">
          <!-- Staff dropdown -->
          <select ref="staffSelectEl" v-model="draft.staffId" :disabled="busy">
            <option value="" disabled>Select staff</option>

            <option v-for="p in staffOptionsNorm" :key="p.id" :value="p.id">
              {{ p.label }}
            </option>
          </select>

          <label class="active"> Staff </label>
        </div>

        <div class="input-field col s12 m6">
          <input id="se_date" type="text" :value="dateLabel" disabled />

          <label class="active" for="se_date"> Date </label>
        </div>
      </div>

      <div class="row" style="margin-bottom: 0">
        <div class="input-field col s12 m6">
          <input id="se_start" v-model="draft.startTime" type="time" :disabled="busy" />

          <label class="active" for="se_start"> Start </label>
        </div>

        <div class="input-field col s12 m6">
          <input id="se_end" v-model="draft.endTime" type="time" :disabled="busy" />

          <label class="active" for="se_end"> End </label>
        </div>
      </div>

      <div class="row" style="margin-bottom: 0">
        <div class="input-field col s12">
          <input id="se_notes" v-model="draft.notes" type="text" :disabled="busy" />

          <label class="active" for="se_notes"> Notes (optional) </label>
        </div>
      </div>

      <div class="grey-text text-darken-1" style="font-size: 12px; margin-top: 6px">
        Selected:
        <b>{{ staffLabel }}</b>
      </div>
    </div>

    <div class="modal-footer">
      <a class="btn-flat" :class="{ disabled: busy }" @click.prevent="close"> Cancel </a>

      <a
        v-if="draft.id"
        class="btn-flat red-text text-darken-2"
        :class="{ disabled: busy }"
        @click.prevent="emitDelete"
      >
        Delete
      </a>

      <a
        class="btn"
        :class="{
          disabled: busy || !canSave,
        }"
        @click.prevent="emitSave"
      >
        Save
      </a>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount, nextTick } from "vue";

/* global M */

const props = defineProps({
  value: {
    type: Object,
    default: null,
  },

  busy: {
    type: Boolean,
    default: false,
  },

  staffOptions: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["save", "delete"]);

const modalEl = ref(null);

const staffSelectEl = ref(null);

let modalInstance = null;

let staffSelectInstance = null;

const localError = ref("");

const draft = ref({
  id: null,

  staffId: "",

  staffName: "",

  date: "",

  startTime: "09:00",

  endTime: "17:00",

  notes: "",
});

function normStaffRow(s) {
  const id = s?.id;

  const name = String(s?.staff_name || s?.name || s?.staffName || "").trim();

  const job = String(s?.job_title || s?.jobTitle || "").trim();

  const rate = Number(s?.pay_rate ?? s?.payRate ?? 0);

  const label = job
    ? `${name} (${job} · £${rate.toFixed(2)}/hr)`
    : `${name} (£${rate.toFixed(2)}/hr)`;

  return {
    id,
    name,
    job,
    rate,
    label,
  };
}

const staffOptionsNorm = computed(() => {
  return (Array.isArray(props.staffOptions) ? props.staffOptions : [])
    .filter((s) => !!s?.id)
    .map(normStaffRow)
    .filter((s) => !!s.name);
});

const staffLabel = computed(() => {
  const id = draft.value.staffId;

  const hit = staffOptionsNorm.value.find((x) => x.id === id);

  return hit?.name || draft.value.staffName || "—";
});

const dateLabel = computed(() => String(draft.value.date || "").trim() || "—");

const canSave = computed(() => {
  if (!String(draft.value.staffId || "").trim()) {
    return false;
  }

  if (!String(draft.value.date || "").trim()) {
    return false;
  }

  const s = String(draft.value.startTime || "").trim();

  const e = String(draft.value.endTime || "").trim();

  return s.length >= 4 && e.length >= 4;
});

function syncFromValue(v) {
  localError.value = "";

  if (!v) {
    return;
  }

  draft.value = {
    id: v.id || null,

    staffId: v.staffId || v.staff_id || v.staff_id_fk || "",

    staffName: v.staffName || v.staff_name || v.staffLabel || "",

    date: v.date || v.ymd || "",

    startTime: v.startTime || "09:00",

    endTime: v.endTime || "17:00",

    notes: v.notes || "",
  };
}

watch(
  () => props.value,

  async (v) => {
    syncFromValue(v);

    await nextTick();

    initStaffSelect();
  },

  {
    immediate: true,
    deep: true,
  }
);

watch(
  () => props.staffOptions,

  async () => {
    await nextTick();

    initStaffSelect();
  },

  {
    deep: true,
  }
);

watch(
  () => draft.value.staffId,

  (id) => {
    const hit = staffOptionsNorm.value.find((x) => x.id === id);

    draft.value.staffName = hit?.name || draft.value.staffName || "";
  }
);

function initStaffSelect() {
  if (!staffSelectEl.value) {
    return;
  }

  if (!window.M?.FormSelect) {
    return;
  }

  try {
    staffSelectInstance?.destroy?.();
  } catch {}

  staffSelectInstance = window.M.FormSelect.init(staffSelectEl.value);
}

function open() {
  if (!modalInstance) {
    console.warn("[SHIFT MODAL] not initialised");

    return;
  }

  localError.value = "";

  modalInstance.open();

  nextTick().then(initStaffSelect);
}

function close() {
  modalInstance?.close();
}

function emitSave() {
  if (!canSave.value) {
    localError.value = "Select staff and enter start/end time.";

    return;
  }

  emit("save", {
    ...draft.value,
  });
}

function emitDelete() {
  if (!draft.value.id) {
    return;
  }

  emit("delete", draft.value.id);

  close();
}

onMounted(() => {
  if (!modalEl.value) {
    return;
  }

  if (!window.M?.Modal) {
    console.warn("[SHIFT MODAL] Materialize Modal not available on window.M.Modal");

    return;
  }

  modalInstance = window.M.Modal.init(modalEl.value, {
    dismissible: true,

    onOpenEnd: () => initStaffSelect(),

    onCloseEnd: () => {
      localError.value = "";
    },
  });

  nextTick().then(initStaffSelect);
});

onBeforeUnmount(() => {
  try {
    staffSelectInstance?.destroy?.();
  } catch {}

  try {
    modalInstance?.destroy?.();
  } catch {}

  staffSelectInstance = null;

  modalInstance = null;
});

defineExpose({
  open,
  close,
});
</script>
